/**
 * Die **Verdichtung einer Erzeugungsanlage** (HM-132, ADR 0040): was ihre
 * Geräte gemeldet haben, je Viertelstunde und Gerät — damit es den Ablauf der
 * Rohschicht (14 Tage) überlebt.
 *
 * **Gespeichert wird der Stand, nicht der Ertrag** — wie bei `energy_15m`: Ein
 * Ertrag entsteht als Differenz zweier Stände desselben Datenpunkts, und die
 * lässt sich jederzeit neu bilden, ein verlorener Stand dagegen nicht. Was
 * sich später **nicht** mehr feststellen lässt, ist ein Rücksprung innerhalb
 * einer Viertelstunde; ihn hält `reset` fest, solange die Rohwerte noch da sind.
 *
 * **Die Leistung steht für Menschen**: Erzeugung positiv (der PV-Eingang meldet
 * sie negativ, ADR 0039), ein Speicher mit Vorzeichen, positiv lädt. So muss
 * keine Lesestelle das Vorzeichen eines Vertrags kennen.
 *
 * Reine Regeln: ohne Datenbank, ohne Uhr.
 */
import { INVERTER_DATAPOINTS } from "../device/inverter.js";
import { STORAGE_DATAPOINTS } from "../device/storage.js";
import type { GenerationAssetKind } from "./generation-asset.js";

/** Welcher Teil eines Geräts zu einer Anlage gehört: der PV-Eingang oder die Batterie. */
export type GenerationAssetPart = "pv" | "battery";

/** Die verabredeten Schlüssel je Teil — `energyOut` und `soc` gibt es nur an der Batterie. */
export const ASSET_PART_KEYS: Readonly<
  Record<
    GenerationAssetPart,
    {
      readonly power: string;
      readonly energyIn: string;
      readonly energyOut: string | null;
      readonly soc: string | null;
    }
  >
> = {
  pv: {
    power: INVERTER_DATAPOINTS.pvPower.key,
    energyIn: INVERTER_DATAPOINTS.pvEnergy.key,
    energyOut: null,
    soc: null,
  },
  battery: {
    power: STORAGE_DATAPOINTS.power.key,
    energyIn: STORAGE_DATAPOINTS.energyCharged.key,
    energyOut: STORAGE_DATAPOINTS.energyDischarged.key,
    soc: STORAGE_DATAPOINTS.soc.key,
  },
};

export const partOfKind = (kind: GenerationAssetKind): GenerationAssetPart =>
  kind === "pv" ? "pv" : "battery";

/**
 * **Welche Schlüssel eines Geräts der Teil einer Anlage sind** (HM-135) — aus
 * seinem Katalog.
 *
 * Bei der Batterie immer die des Speichervertrags. Bei PV zuerst der PV-Eingang
 * (`pv-power`, `pv-energy`); **fehlt er, bei einem reinen PV-Wechselrichter die
 * Netzseite** (`ac-power`, `ac-energy-export`): Was ein Wechselrichter ohne
 * Batterie ans Haus abgibt, ist sein Ertrag — gemessen nach der Wandlung, so wie
 * Hersteller den Ertrag je kWp angeben. Ein SMA über SunSpec meldet keinen
 * PV-Ertragsstand, nur diesen. **Mit Batteriekanal nie**: Dort ist die Netzseite
 * PV plus Entladung minus Ladung (ADR 0039 §3) und kein Ertrag.
 *
 * Je Schlüssel einzeln: Ein Gerät mit `pv-power` und nur `ac-energy-export`
 * bekommt die Leistung vom PV-Eingang und den Ertrag von der Netzseite. Beide
 * Leistungen sind negativ bei Erzeugung — dieselbe Umkehr gilt für beide.
 */
export function assetPartKeys(
  part: GenerationAssetPart,
  catalogKeys: readonly string[],
): (typeof ASSET_PART_KEYS)[GenerationAssetPart] {
  if (part === "battery") return ASSET_PART_KEYS.battery;
  const keys = new Set(catalogKeys);
  const hasBattery = Object.values(STORAGE_DATAPOINTS).some((spec) => keys.has(spec.key));
  const fallback = (primary: string, ac: string): string =>
    keys.has(primary) || hasBattery || !keys.has(ac) ? primary : ac;
  return {
    ...ASSET_PART_KEYS.pv,
    power: fallback(INVERTER_DATAPOINTS.pvPower.key, INVERTER_DATAPOINTS.acPower.key),
    energyIn: fallback(INVERTER_DATAPOINTS.pvEnergy.key, INVERTER_DATAPOINTS.acEnergyExport.key),
  };
}

/** Ein Datenpunkt eines Geräts in einer Viertelstunde, so wie die Rohschicht ihn verdichtet. */
export interface AssetRawBucket {
  /** Beginn der Viertelstunde, ms. */
  readonly at: number;
  readonly key: string;
  readonly mean: number;
  readonly min: number;
  readonly max: number;
  /** Der Wert zum spätesten Messzeitpunkt — bei einem Stand der Stand am Abschnittsende. */
  readonly last: number;
  readonly count: number;
}

/** Eine Zeile der Verdichtung: ein Gerät einer Anlage in einer Viertelstunde. */
export interface AssetRollupRow {
  readonly at: number;
  /** Stand am Abschnittsende: PV-Ertrag bzw. geladen, Wh. */
  readonly energyInWh: number | null;
  /** Stand am Abschnittsende: entladen, Wh — nur an der Batterie. */
  readonly energyOutWh: number | null;
  readonly powerMeanW: number | null;
  readonly powerMinW: number | null;
  readonly powerMaxW: number | null;
  /** Ladezustand am Abschnittsende, 0…1 — nur an der Batterie. */
  readonly soc: number | null;
  readonly samples: number;
  /** Ein Stand ist in dieser Viertelstunde oder gegenüber der vorigen zurückgesprungen. */
  readonly reset: boolean;
}

/** Die Stände der Viertelstunde davor — für den Rücksprung über die Abschnittsgrenze. */
export interface AssetPreviousStands {
  readonly energyInWh: number | null;
  readonly energyOutWh: number | null;
}

/**
 * Macht aus den Abschnittswerten eines Geräts die Zeilen der Verdichtung.
 * Eine Viertelstunde, in der keiner der Schlüssel des Teils einen Wert hat,
 * ergibt **keine** Zeile — eine Lücke bleibt eine Lücke (Regel 8).
 */
export function assetRollupRows(
  part: GenerationAssetPart,
  raw: readonly AssetRawBucket[],
  previous: AssetPreviousStands | null,
  /** Die Schlüssel dieses Geräts (`assetPartKeys`) — ohne Angabe die des Vertrags. */
  keys: (typeof ASSET_PART_KEYS)[GenerationAssetPart] = ASSET_PART_KEYS[part],
): AssetRollupRow[] {
  const relevant = new Set(
    [keys.power, keys.energyIn, keys.energyOut, keys.soc].filter((key): key is string => key !== null),
  );
  const byBucket = new Map<number, Map<string, AssetRawBucket>>();
  for (const bucket of raw) {
    if (!relevant.has(bucket.key)) continue;
    const entry = byBucket.get(bucket.at) ?? new Map<string, AssetRawBucket>();
    entry.set(bucket.key, bucket);
    byBucket.set(bucket.at, entry);
  }

  const rows: AssetRollupRow[] = [];
  let before: AssetPreviousStands = previous ?? { energyInWh: null, energyOutWh: null };
  for (const at of [...byBucket.keys()].sort((a, b) => a - b)) {
    const entry = byBucket.get(at)!;
    const power = entry.get(keys.power);
    const energyIn = entry.get(keys.energyIn);
    const energyOut = keys.energyOut === null ? undefined : entry.get(keys.energyOut);
    const soc = keys.soc === null ? undefined : entry.get(keys.soc);

    const fell = (stand: AssetRawBucket | undefined, prior: number | null): boolean =>
      stand !== undefined &&
      (stand.last < stand.max || (prior !== null && stand.min < prior));

    /* Die Erzeugung ist positiv; der PV-Eingang meldet sie negativ — Minimum und Maximum tauschen. */
    const human =
      power === undefined
        ? { mean: null, min: null, max: null }
        : part === "pv"
          ? {
              mean: Math.max(-power.mean, 0),
              min: Math.max(-power.max, 0),
              max: Math.max(-power.min, 0),
            }
          : { mean: power.mean, min: power.min, max: power.max };

    const row: AssetRollupRow = {
      at,
      energyInWh: energyIn?.last ?? null,
      energyOutWh: energyOut?.last ?? null,
      powerMeanW: human.mean,
      powerMinW: human.min,
      powerMaxW: human.max,
      soc: soc === undefined || soc.last < 0 || soc.last > 1 ? null : soc.last,
      samples: Math.max(...[...entry.values()].map((bucket) => bucket.count)),
      reset: fell(energyIn, before.energyInWh) || fell(energyOut, before.energyOutWh),
    };
    rows.push(row);
    before = {
      energyInWh: row.energyInWh ?? before.energyInWh,
      energyOutWh: row.energyOutWh ?? before.energyOutWh,
    };
  }
  return rows;
}

/** Ein Stand in der Verdichtung, wie ihn die Lesestelle sieht. */
export interface AssetStand {
  readonly at: number;
  readonly value: number;
  readonly reset: boolean;
}

/**
 * **Ein Stand, der kurz einbricht und sich erholt, ist ein Messfehler** — kein
 * Zählertausch. Ein Victron-PV-Wechselrichter meldet beim Aufwachen am Morgen
 * einmal 0 Wh als Gesamtertrag und eine halbe Stunde später wieder 2 656 600:
 * Gegen den Rücksprung (Regel 4) war damit jeder Tag dieser Anlage unbekannt,
 * und mit ihm die PV-Summe der ganzen Anlage.
 *
 * Herausgenommen wird ein Stand, der unter dem letzten gültigen liegt, wenn ein
 * **späterer** Stand der Reihe diesen wieder erreicht — samt seiner
 * Rücksprungmarke, denn die hat die Verdichtung genau aus diesem Einbruch
 * gesetzt. Ein echter Zählertausch erholt sich nicht: Der neue Zähler bleibt
 * unter dem alten, und der Rücksprung gilt weiter. Steht die Erholung noch
 * aus (der Einbruch ist der jüngste Stand), bleibt er stehen — wer ihn heute
 * für einen Fehler hielte, riete (Regel 9).
 *
 * Erwartet die Stände **eines** Geräts, aufsteigend nach Zeit — am besten mit
 * denen vor dem Fenster, damit auch ein Einbruch als letzter Stand davor nicht
 * zum Ausgangswert wird.
 */
export function withoutTransientDips(stands: readonly AssetStand[]): AssetStand[] {
  const kept: AssetStand[] = [];
  let last: number | null = null;
  for (const [index, stand] of stands.entries()) {
    if (last !== null && stand.value < last) {
      const reference = last;
      const recovers = stands.slice(index + 1).some((later) => later.value >= reference);
      if (recovers) continue;
    }
    kept.push(stand);
    last = stand.value;
  }
  return kept;
}

/**
 * Der **Zuwachs eines Stands über ein Fenster** aus der Verdichtung: letzter
 * Stand im Fenster minus der letzte davor — oder, ohne Stand davor, der erste
 * im Fenster (dann fehlt der Anteil der ersten Viertelstunde, und `partial`
 * sagt es). Springt der Stand irgendwo zurück, ist der Zuwachs **unbekannt**
 * und nicht kleiner (Regel 4).
 *
 * **Über eine fremde Lücke wird nicht gezählt** (HM-144): `breaks` sind die
 * Lücken des Geräts, in denen ein anderes für die Anlage geschrieben hat
 * (`assetMembershipBreaks`). Der Zuwachs über eine solche Lücke gehört dem
 * anderen Gerät — meldet dieselbe Batterie unter zwei Kennungen, stünde er
 * sonst zweimal da. Gezählt wird davor und danach, und beides addiert.
 */
export function assetStandIncrease(
  stands: readonly AssetStand[],
  before: number | null,
  options: { readonly beforeAt?: number | null; readonly breaks?: readonly AssetMembershipBreak[] } = {},
): { readonly increase: number | null; readonly partial: boolean } {
  if (stands.length === 0) return { increase: null, partial: false };
  const breaks = options.breaks ?? [];
  const crossesBreak = (fromAt: number | null, toAt: number): boolean =>
    fromAt !== null && breaks.some((gap) => gap.after >= fromAt && gap.before <= toAt);
  let prior = before;
  let priorAt = before === null ? null : (options.beforeAt ?? null);
  let increase = 0;
  for (const stand of stands) {
    if (stand.reset) return { increase: null, partial: false };
    if (prior !== null && !crossesBreak(priorAt, stand.at)) {
      if (stand.value < prior) return { increase: null, partial: false };
      increase += stand.value - prior;
    }
    prior = stand.value;
    priorAt = stand.at;
  }
  return { increase, partial: before === null };
}

/**
 * **Wo ein Gerät nicht zur Anlage gehörte** (HM-144). Die Verdichtung hat
 * keine Gültigkeit je Zuordnung; ihre Zeilen sind der Nachweis. Eine Lücke in
 * den Zeilen eines Geräts heißt deshalb zweierlei: Es hat geschwiegen, während
 * es dazugehörte — dann misst sein Stand die Lücke richtig mit —, oder es
 * gehörte nicht dazu, weil ein anderes an seiner Stelle schrieb. Das Zweite
 * ist ein **Bruch**: Hat in der Lücke ein anderes Gerät geschrieben, das
 * nicht heute zugleich mit ihm zugeordnet ist, gehört die Zeit dem anderen.
 *
 * Zwei heute zugeordnete Geräte (zwei Mikrowechselrichter) brechen einander
 * nie — dass eines schweigt, während das andere schreibt, ist ihr Normalfall.
 *
 * `at` je Gerät sind die Beginne der geschriebenen Viertelstunden, aufsteigend.
 * Ein Bruch liegt **zwischen** `after` und `before`, beides eigene Zeilen.
 */
export interface AssetMembershipBreak {
  readonly after: number;
  readonly before: number;
}

export interface AssetDeviceRows {
  readonly deviceId: string;
  readonly assigned: boolean;
  readonly at: readonly number[];
}

export function assetMembershipBreaks(
  devices: readonly AssetDeviceRows[],
  bucketMs: number,
): ReadonlyMap<string, readonly AssetMembershipBreak[]> {
  const result = new Map<string, AssetMembershipBreak[]>();
  for (const device of devices) {
    const others = devices.filter(
      (other) => other.deviceId !== device.deviceId && !(device.assigned && other.assigned),
    );
    const breaks: AssetMembershipBreak[] = [];
    for (let index = 1; index < device.at.length; index++) {
      const after = device.at[index - 1]!;
      const before = device.at[index]!;
      if (before - after <= bucketMs) continue;
      if (others.some((other) => other.at.some((at) => at > after && at < before))) {
        breaks.push({ after, before });
      }
    }
    result.set(device.deviceId, breaks);
  }
  return result;
}

/**
 * **Wie weit ein Gerät beim Nachholen zurückreicht** (HM-144). Ein Gerät, das
 * neu zu einer Anlage kommt, bekommt seine Rohwerte bis an die
 * Aufbewahrungsgrenze nachgetragen (HM-132). Seit die Werte eines früheren
 * Geräts in der Anlage bleiben, darf dieses Nachholen nicht in die Zeit
 * reichen, für die schon ein anderes **früheres** Gerät geschrieben hat:
 *
 * - **Ein Tausch an derselben Anlage** — der neue Wechselrichter bekäme sonst
 *   dieselben Tage noch einmal, und die Summe zählte sie doppelt.
 * - **Ein Umzug desselben Geräts zu einer anderen Anlage** (gleicher Teil) —
 *   sonst stünde derselbe Ertrag in beiden Anlagen.
 *
 * Früher heißt: heute nicht mehr an dieser Anlage. Ein Gerät, das daneben
 * weiterhin dazugehört (zwei Mikrowechselrichter), begrenzt nichts; ein Hybrid
 * an einer PV-Anlage und einem Speicher auch nicht, denn das sind zwei Teile.
 *
 * Zurückgegeben wird der früheste Abschnittsbeginn, ab dem nachgeholt werden
 * darf — das Ende der jüngsten fremden Viertelstunde —, oder `null` ohne Grenze.
 */
export interface AssetWrittenSpan {
  readonly assetId: string;
  readonly deviceId: string;
  readonly part: GenerationAssetPart;
  /** Beginn der zuletzt geschriebenen Viertelstunde, Millisekunden. */
  readonly lastAt: number;
}

export function assetBackfillFloor(
  pair: { readonly assetId: string; readonly deviceId: string; readonly part: GenerationAssetPart },
  written: readonly AssetWrittenSpan[],
  isAssigned: (assetId: string, deviceId: string) => boolean,
  bucketMs: number,
): number | null {
  let floor: number | null = null;
  for (const span of written) {
    if (isAssigned(span.assetId, span.deviceId)) continue;
    const replaced = span.assetId === pair.assetId && span.deviceId !== pair.deviceId;
    const moved =
      span.deviceId === pair.deviceId && span.assetId !== pair.assetId && span.part === pair.part;
    if (!replaced && !moved) continue;
    const end = span.lastAt + bucketMs;
    if (floor === null || end > floor) floor = end;
  }
  return floor;
}
